import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  Mail,
  Phone,
  Building2,
  Calendar,
  User,
  FileText,
  ShieldCheck,
  Clock,
  Users,
  QrCode,
  X,
} from "lucide-react";
import "../styles/admin-base.css";
import "../styles/members.css";
import "../../User Side/styles/digital-id.css";
import { mockMembers } from "./mockData";
import { getAdminMember } from "../../src/api/admin";
import { listTickets, listVenueBookings } from "../api/admin";
import QRCode from "qrcode";
import { resolveQrReference } from "../../src/utils/qr";
import { getUnionBranding } from "../../src/utils/unionBranding";
import { API_BASE_URL } from "../../src/config";
import { resolveBackendAssetUrl } from "../../src/utils/assetUrl";
import { getMembershipFormPreviewUrl } from "../utils/exportMembershipPdf";

const ACTIVE_TICKET_STATUSES = new Set(["open", "in_progress", "pending", "under_review"]);
const ACTIVE_BOOKING_STATUSES = new Set(["pending_approval", "approved", "upcoming"]);

const normalizeStatusKey = (value) => {
  if (!value) return "";
  return String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
};

const toTitleCaseWords = (value) => {
  if (!value) return "—";
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDateTimeValue = (value, options) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", options);
};

const formatClockTime = (value) => {
  if (!value) return "—";
  const normalized = String(value).trim();
  const match = normalized.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return normalized;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return normalized;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatTimeRange = (startTime, endTime) => {
  if (!startTime && !endTime) return "—";
  if (!startTime) return formatClockTime(endTime);
  if (!endTime) return formatClockTime(startTime);
  return `${formatClockTime(startTime)} - ${formatClockTime(endTime)}`;
};

const getNameStyle = (fullName) => {
  const length = (fullName || '').length;
  let fontSize = '1.5rem';
  if (length > 35) fontSize = '0.75rem';
  else if (length > 28) fontSize = '0.9rem';
  else if (length > 22) fontSize = '1.1rem';
  else if (length > 18) fontSize = '1.25rem';
  else if (length > 14) fontSize = '1.35rem';
  return {
    fontSize, whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip',
    width: '100%', display: 'block', textAlign: 'center', lineHeight: '1.2',
    margin: 0, letterSpacing: '0.05em', color: '#0f172a', fontWeight: 700,
  };
};

const formatDate = (value, options) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", options);
};

const formatYearsEmployed = (value) => {
  const num = Number(value);
  if (Number.isFinite(num)) return String(Math.trunc(num));
  return value ?? "—";
};

const defaultDocuments = [
  { name: "Government ID", type: "Identification", uploadedAt: "2025-01-12" },
  { name: "Employment Certificate", type: "Employment", uploadedAt: "2025-01-10" },
  { name: "Union Membership Form", type: "Membership", uploadedAt: "2024-12-01" },
];

const resolveFileBaseUrl = () => {
  return API_BASE_URL;
};

const resolveDocumentUrl = (path) => {
  return resolveBackendAssetUrl(path, { apiBaseUrl: resolveFileBaseUrl() });
};

const IMAGE_EXTENSION_REGEX = /\.(png|jpe?g|gif|bmp|webp|svg)$/i;

const isImageDocument = (doc = {}, url = "") => {
  const type = String(doc.type ?? "").toLowerCase();
  const name = String(doc.name ?? "");
  const filePath = String(doc.filePath ?? "");
  const lowerUrl = String(url ?? "").toLowerCase();

  if (type.includes("image") || type.includes("photo") || type.includes("signature")) {
    return true;
  }
  if (IMAGE_EXTENSION_REGEX.test(name) || IMAGE_EXTENSION_REGEX.test(filePath) || IMAGE_EXTENSION_REGEX.test(lowerUrl)) {
    return true;
  }
  return false;
};

const getVisibleDocumentStatus = (status) => {
  if (!status) return "";
  const normalized = normalizeStatusKey(status);
  if (normalized === "pending_review") {
    return "";
  }
  return String(status);
};

export default function MemberProfile() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [previewingForm, setPreviewingForm] = useState(false);
  const [formPreview, setFormPreview] = useState({
    open: false,
    url: "",
    error: "",
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [mediaModal, setMediaModal] = useState({
    open: false,
    src: "",
    label: "",
    filePath: "",
    uploadedAt: "",
    source: "",
  });

  const openMediaModal = useCallback((src, label, meta = {}) => {
    if (!src) return;
    setMediaModal({
      open: true,
      src,
      label,
      filePath: meta.filePath ?? "",
      uploadedAt: meta.uploadedAt ?? "",
      source: meta.source ?? "",
    });
  }, []);

  const closeMediaModal = useCallback(() => {
    setMediaModal({
      open: false,
      src: "",
      label: "",
      filePath: "",
      uploadedAt: "",
      source: "",
    });
  }, []);

  useEffect(() => {
    if (!mediaModal.open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMediaModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mediaModal.open, closeMediaModal]);

  useEffect(() => {
    let isMounted = true;

    const fetchMember = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getAdminMember(memberId);
        const data = response?.data?.member ?? response?.data;
        if (isMounted) {
          if (data) {
            setMember(data);
          } else {
            setError("Member record not found.");
          }
        }
      } catch (apiError) {
        const fallback = mockMembers.find((entry) => entry.id === memberId || entry.memberID === memberId);
        if (isMounted) {
          if (fallback) {
            setMember(fallback);
          } else {
            setError("Member record not found.");
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMember();
    return () => {
      isMounted = false;
    };
  }, [memberId]);

  const memberIdentity = useMemo(() => {
    if (!member) {
      return {
        ids: new Set(),
        text: new Set(),
      };
    }

    const idCandidates = [
      member.id,
      member.userId,
      member.memberId,
      member.memberID,
      member.membershipNo,
      member.membershipNumber,
    ]
      .map((value) => (value == null ? "" : String(value).trim()))
      .filter(Boolean);

    const textCandidates = [
      member.fullName,
      member.email,
      member.memberID,
      member.membershipNo,
    ]
      .map((value) => (value == null ? "" : String(value).trim().toLowerCase()))
      .filter(Boolean);

    return {
      ids: new Set(idCandidates),
      text: new Set(textCandidates),
    };
  }, [member]);

  const isTicketActive = useCallback((ticket) => {
    const status = normalizeStatusKey(ticket?.status);
    return ACTIVE_TICKET_STATUSES.has(status);
  }, []);

  const isBookingActive = useCallback((booking) => {
    const status = normalizeStatusKey(booking?.status);
    if (ACTIVE_BOOKING_STATUSES.has(status)) {
      return true;
    }
    if (status === "pending") {
      return true;
    }
    if (status === "approved" && booking?.bookingDate) {
      const bookingDate = new Date(booking.bookingDate);
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return !Number.isNaN(bookingDate.getTime()) && bookingDate >= startOfToday;
    }
    return false;
  }, []);

  const ticketBelongsToMember = useCallback(
    (ticket) => {
      if (!ticket || !member) return false;
      const idCandidates = [
        ticket.userId,
        ticket.user_id,
        ticket.memberId,
        ticket.member_id,
        ticket.user?.id,
        ticket.member?.id,
        ticket.user?.memberID,
        ticket.user?.membershipNo,
      ]
        .map((value) => (value == null ? "" : String(value).trim()))
        .filter(Boolean);
      if (idCandidates.some((candidate) => memberIdentity.ids.has(candidate))) {
        return true;
      }

      const textCandidates = [
        ticket.userName,
        ticket.memberName,
        ticket.user?.name,
        ticket.user?.fullName,
        ticket.userEmail,
        ticket.user?.email,
        ticket.membershipNo,
      ]
        .map((value) => (value == null ? "" : String(value).trim().toLowerCase()))
        .filter(Boolean);

      return textCandidates.some((candidate) => memberIdentity.text.has(candidate));
    },
    [member, memberIdentity.ids, memberIdentity.text],
  );

  const bookingBelongsToMember = useCallback(
    (booking) => {
      if (!booking || !member) return false;
      const idCandidates = [
        booking.userId,
        booking.user_id,
        booking.memberId,
        booking.member_id,
        booking.customerId,
        booking.membershipNo,
        booking.memberID,
      ]
        .map((value) => (value == null ? "" : String(value).trim()))
        .filter(Boolean);
      if (idCandidates.some((candidate) => memberIdentity.ids.has(candidate))) {
        return true;
      }

      const textCandidates = [
        booking.customerName,
        booking.email,
        booking.membershipNo,
      ]
        .map((value) => (value == null ? "" : String(value).trim().toLowerCase()))
        .filter(Boolean);

      return textCandidates.some((candidate) => memberIdentity.text.has(candidate));
    },
    [member, memberIdentity.ids, memberIdentity.text],
  );

  useEffect(() => {
    if (!member) {
      setRecentActivities([]);
      setActivitiesLoading(false);
      return;
    }

    let isMounted = true;

    const loadRecentActivity = async () => {
      setActivitiesLoading(true);
      try {
        const [ticketsResult, bookingsResult] = await Promise.allSettled([
          listTickets({ pageSize: 200 }),
          listVenueBookings({ pageSize: 200 }),
        ]);

        const tickets =
          ticketsResult.status === "fulfilled"
            ? ticketsResult.value?.data?.results ?? []
            : [];
        const bookings =
          bookingsResult.status === "fulfilled"
            ? bookingsResult.value?.data?.results ?? []
            : [];

        const ticketActivities = tickets
          .filter((ticket) => isTicketActive(ticket) && ticketBelongsToMember(ticket))
          .map((ticket) => {
            const createdAt = ticket.createdAt ?? ticket.created_at ?? null;
            return {
              type: "ticket",
              sortAt: createdAt ? new Date(createdAt).getTime() : 0,
              createdAt,
              id: ticket.id ?? ticket.ticketNo ?? ticket.ticketId,
              ticket,
            };
          });

        const bookingActivities = bookings
          .filter((booking) => isBookingActive(booking) && bookingBelongsToMember(booking))
          .map((booking) => {
            const createdAt = booking.createdAt ?? booking.created_at ?? booking.bookingDate ?? null;
            return {
              type: "booking",
              sortAt: createdAt ? new Date(createdAt).getTime() : 0,
              createdAt,
              id: booking.id ?? booking.bookingId,
              booking,
            };
          });

        const merged = [...ticketActivities, ...bookingActivities].sort((a, b) => b.sortAt - a.sortAt);

        if (isMounted) {
          setRecentActivities(merged);
        }
      } catch (activityError) {
        if (isMounted) {
          setRecentActivities([]);
        }
      } finally {
        if (isMounted) {
          setActivitiesLoading(false);
        }
      }
    };

    loadRecentActivity();

    return () => {
      isMounted = false;
    };
  }, [
    member,
    isTicketActive,
    isBookingActive,
    ticketBelongsToMember,
    bookingBelongsToMember,
  ]);

  const stats = useMemo(() => {
    if (!member) return [];
    const registeredDate = member.registeredDate ?? member.joinDate;
    const statusCandidates = [member.idStatus, member.digitalIdStatus, member.cardStatus]
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean);
    const digitalIdStatusSummary =
      statusCandidates.find((status) => /released|issued|delivered|active|ready/i.test(status)) ??
      statusCandidates[0] ??
      (member.digitalID ? "Issued" : "Not Issued");

    return [
      {
        label: "Member since",
        value: formatDate(registeredDate, { month: "long", year: "numeric" }),
        tone: "is-blue",
      },
      {
        label: "Union role",
        value: member.unionPosition ?? "Member",
        tone: "is-green",
      },
      {
        label: "Digital ID",
        value: digitalIdStatusSummary,
        tone: "is-purple",
      },
    ];
  }, [member]);

  const emergencyContact = useMemo(() => {
    if (!member?.emergencyContact) {
      return null;
    }
    if (typeof member.emergencyContact === "string") {
      return {
        fullName: member.emergencyContact,
        relationship: "",
        phone: "",
        address: "",
      };
    }
    return {
      fullName: member.emergencyContact.fullName ?? member.emergencyContact.name ?? "",
      relationship: member.emergencyContact.relationship ?? "",
      phone: member.emergencyContact.phone ?? member.emergencyContact.contactNo ?? "",
      address: member.emergencyContact.address ?? "",
    };
  }, [member?.emergencyContact]);

  const memberInitials = useMemo(() => {
    if (!member?.fullName) return "AL";
    return member.fullName
      .split(" ")
      .filter(Boolean)
      .map((name) => name[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [member?.fullName]);

  const statusCandidates = [member?.idStatus, member?.digitalIdStatus, member?.cardStatus]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean);
  const resolvedIdStatus =
    statusCandidates.find((status) => /released|issued|delivered|active|ready/i.test(status)) ??
    statusCandidates[0] ??
    (member?.digitalID ? "Issued" : "Not Issued");
  const normalizedIdStatus = String(resolvedIdStatus).toLowerCase();
  const idIssued =
    !normalizedIdStatus.includes("not issued") &&
    ["issued", "released", "delivered", "active", "ready"].some((token) => normalizedIdStatus.includes(token));
  const idPhotoSource = resolveDocumentUrl(
    member?.idPhotoPath ??
      member?.photoUrl ??
      member?.photoURL ??
      member?.profilePhoto ??
      member?.photo ??
      member?.avatarUrl ??
      member?.profileImage ??
      member?.idPhoto ??
      null,
  );
  const physicalCardStatus = member?.cardStatus ?? (member?.cardRequested ? "Requested" : "Not requested");
  const idLastUpdated =
    member?.idUpdatedAt ?? member?.idIssuedAt ?? member?.updatedAt ?? member?.registeredDate ?? member?.joinDate;
  const idRequestReference =
    member?.cardRequestNo ??
    member?.digitalQrReference ??
    member?.cardReference ??
    member?.cardTicket ??
    "—";

  // --- Card flip & QR ---
  const [showBack, setShowBack] = useState(false);
  const [generatedQr, setGeneratedQr] = useState("");

  const cardMemberId = member?.memberID || member?.digitalID || "LWU-000000";
  const shortMemberId = cardMemberId.split("-").pop() ?? cardMemberId;
  const memberPosition = member?.unionPosition || member?.position || "Member";
  const cardCompanyName = member?.company || "LWU Member";
  const cardUnionName = member?.unionAffiliation || "LWU";
  const branding = getUnionBranding(member?.unionAffiliation);
  const memberPhone = member?.mobile || member?.phone || "N/A";
    const emergencyContactName = (
      member?.emergencyContact?.name
      || member?.emergencyContactName
      || ""
    ).toString().trim() || "Not Provided";
    const emergencyContactMobile = (
      member?.emergencyContact?.phone
      || member?.emergencyContactPhone
      || member?.emergencyContactMobile
      || member?.emergencyContactNumber
      || ""
    ).toString().trim() || "Not Provided";
  const signatureImage = resolveDocumentUrl(
    member?.signaturePath ??
      member?.signatureUrl ??
      member?.signatureURL ??
      member?.signature ??
      null,
  );

  const qrReference = useMemo(() => {
    const refVal = member?.digitalQrReference || member?.cardReference;
    if (refVal) return refVal;
    return resolveQrReference({
      id: member?.id,
      digitalId: member?.digitalID || member?.memberID,
      qrReference: member?.digitalQrReference,
    });
  }, [member]);

  const qrPayload = useMemo(() => {
    if (!member) return null;
    return {
      type: "ALU_MEMBER_ID",
      version: 1,
      memberId: cardMemberId,
      shortId: shortMemberId,
      userId: member.id,
      fullName: member.fullName,
      company: cardCompanyName,
      union: cardUnionName,
      qrReference,
    };
  }, [member, cardMemberId, shortMemberId, cardCompanyName, cardUnionName, qrReference]);

  const qrDataString = useMemo(() => (qrPayload ? JSON.stringify(qrPayload) : ""), [qrPayload]);

  useEffect(() => {
    let cancelled = false;
    if (!qrDataString) {
      setGeneratedQr("");
      return () => { cancelled = true; };
    }
    QRCode.toDataURL(qrDataString, { width: 280, margin: 1 })
      .then((url) => { if (!cancelled) setGeneratedQr(url); })
      .catch(() => { if (!cancelled) setGeneratedQr(""); });
    return () => { cancelled = true; };
  }, [qrDataString]);

  const closeFormPreview = useCallback(() => {
    setFormPreview((current) => {
      if (current.url) {
        try {
          window.URL.revokeObjectURL(current.url);
        } catch (_err) {
          // no-op
        }
      }
      return {
        open: false,
        url: "",
        error: "",
      };
    });
  }, []);

  const handlePreviewForm = useCallback(async () => {
    if (!member || previewingForm) return;
    try {
      setPreviewingForm(true);
      setError("");
      setFormPreview({ open: true, url: "", error: "" });
      const previewUrl = await getMembershipFormPreviewUrl(member);
      setFormPreview((current) => {
        if (current.url) {
          try {
            window.URL.revokeObjectURL(current.url);
          } catch (_err) {
            // no-op
          }
        }
        return {
          open: true,
          url: previewUrl,
          error: "",
        };
      });
    } catch (pdfError) {
      console.error("Failed to preview membership form", pdfError);
      setFormPreview({
        open: true,
        url: "",
        error:
          pdfError.message ??
          "Unable to generate form preview. Ensure the membership form template exists in public/assets/membership-form-template.png.",
      });
    } finally {
      setPreviewingForm(false);
    }
  }, [member, previewingForm]);

  useEffect(() => {
    return () => {
      if (formPreview.url) {
        try {
          window.URL.revokeObjectURL(formPreview.url);
        } catch (_err) {
          // no-op
        }
      }
    };
  }, [formPreview.url]);

  if (loading) {
    return (
      <div className="admin-page admin-stack-lg member-profile-page">
        <div className="admin-empty-state">Loading member details…</div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="admin-page admin-stack-lg member-profile-page">
        <button type="button" className="admin-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
          Back to members
        </button>
        <div className="admin-empty-state">{error || "Member record unavailable."}</div>
      </div>
    );
  }

  const documents = member.documents ?? defaultDocuments;
  const fileBaseUrl = resolveFileBaseUrl();

  return (
    <div className="admin-page admin-stack-lg member-profile-page">
      <header className="admin-surface member-profile-hero">
        <div className="member-profile-hero__intro">
          <button
            type="button"
            className="member-profile-hero__back-link"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <div>
            <h1>Member Profile</h1>
            <p className="admin-muted">
              Review identity details, documents, ID status, and recent member activity in one place.
            </p>
          </div>
        </div>
        <div className="admin-actions member-profile-hero__actions">
          <button
            type="button"
            className="admin-button"
            onClick={() => navigate(`/admin/id-card-management?member=${encodeURIComponent(member.memberID)}`)}
          >
            <CreditCard size={16} /> Issue ID
          </button>
          <button
            type="button"
            className="admin-button is-primary"
            onClick={handlePreviewForm}
            disabled={previewingForm}
          >
            <FileText size={16} /> {previewingForm ? "Preparing preview..." : "Preview Form"}
          </button>
        </div>
      </header>

      <section className="admin-surface admin-stack-md member-profile-section member-profile-section--identity">
        <div className="admin-profile-banner">
          <div className="admin-profile-banner__summary">
            <div>
              <h2>{member.fullName}</h2>
              <p className="admin-muted">Member ID: {member.memberID ?? "—"} • {member.position ?? "Union Member"}</p>
              <div className="admin-contact-row">
                <span>
                  <Mail size={14} />
                  {member.email}
                </span>
                <span>
                  <Phone size={14} />
                  {member.mobile}
                </span>
                <span>
                  <ShieldCheck size={14} />
                  {member.status}
                </span>
              </div>
            </div>
          </div>
          <div className="admin-profile-banner__meta">
            <div>
              <strong>Union Affiliation</strong>
              <p>{member.unionAffiliation ?? "—"}</p>
            </div>
            <div>
              <strong>Company</strong>
              <p>{member.company ?? "—"}</p>
            </div>
          </div>
        </div>

        <div className="admin-card-grid cols-4 admin-card-grid--spaced">
          {stats.map((item) => (
            <article key={item.label} className="admin-card member-profile-kpi-card">
              <div className="admin-card__label">{item.label}</div>
              <div className="admin-card__value">{item.value}</div>
            </article>
          ))}
        </div>
      </section>

      <section className="admin-surface admin-stack-md member-profile-section member-profile-section--id">
        <div className="admin-section-heading">
          <span className="admin-heading-icon admin-heading-icon--dues">
            <CreditCard size={16} />
          </span>
          <div>
            <h2>Member ID</h2>
            <p className="admin-muted">Visual preview and issuance status for this member.</p>
          </div>
        </div>
        <div className="admin-id-grid admin-id-grid--spaced">
          <div
            className={`digital-id-card${showBack ? ' digital-id-card--flipped' : ''}`}
            onClick={() => setShowBack((prev) => !prev)}
            role="presentation"
          >
            <div className="digital-id-card__inner">
              {/* FRONT */}
              <div className="digital-id-card__face digital-id-card__face--front">
                <div className="digital-id-card__header" style={{ background: branding.colors.headerGradient }}>
                  <div className="digital-id-card__seal">
                    <img src={branding.logo} alt={branding.shortName} />
                  </div>
                  <div>
                    <p style={{ fontSize: branding.fullName.length > 30 ? '0.78rem' : '0.9rem' }}>{branding.fullName.toUpperCase()}</p>
                    <span>No. 262, 15th Ave., Brgy. Silangan, Cubao, Quezon City</span>
                  </div>
                </div>
                <div className="digital-id-card__member-banner">{memberPosition.toUpperCase()}</div>
                <div className="digital-id-card__body">
                  <div className="digital-id-card__photo">
                    {idPhotoSource ? (
                      <button
                        type="button"
                        className="members-id-photo-trigger"
                        onClick={(event) => {
                          event.stopPropagation();
                          openMediaModal(idPhotoSource, "Member ID Photo", {
                            filePath:
                              member?.idPhotoPath ??
                              member?.photoUrl ??
                              member?.photoURL ??
                              member?.profilePhoto ??
                              member?.photo ??
                              member?.avatarUrl ??
                              member?.profileImage ??
                              member?.idPhoto ??
                              "",
                            uploadedAt: member?.idUpdatedAt ?? member?.updatedAt ?? member?.registeredDate ?? "",
                            source: "Member profile",
                          });
                        }}
                        aria-label="View ID photo"
                      >
                        <img src={idPhotoSource} alt="Member" />
                      </button>
                    ) : (
                      <div className="digital-id-card__initials">{memberInitials}</div>
                    )}
                  </div>
                  <div className="digital-id-card__info">
                    {(() => {
                      const displayName = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.fullName || '';
                      return (
                        <div style={getNameStyle(displayName)}>
                          {displayName.toUpperCase()}
                        </div>
                      );
                    })()}
                    <div className="members-id-position-text">
                      {memberPosition}
                    </div>
                    <div className="digital-id-card__meta">
                      <table className="members-id-meta-table">
                        <tbody>
                          <tr>
                            <td>ID Number</td>
                            <td>:</td>
                            <td>{shortMemberId}</td>
                          </tr>
                          <tr>
                            <td>Company</td>
                            <td>:</td>
                            <td>{cardCompanyName}</td>
                          </tr>
                          <tr>
                            <td>Contact No.</td>
                            <td>:</td>
                            <td>{memberPhone}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="digital-id-card__signature">
                      {signatureImage && (
                        <button
                          type="button"
                          className="members-id-signature-trigger"
                          onClick={(event) => {
                            event.stopPropagation();
                            openMediaModal(signatureImage, "Authorized Signature", {
                              filePath:
                                member?.signaturePath ??
                                member?.signatureUrl ??
                                member?.signatureURL ??
                                member?.signature ??
                                "",
                              uploadedAt: member?.updatedAt ?? member?.registeredDate ?? "",
                              source: "Member profile",
                            });
                          }}
                          aria-label="View signature"
                        >
                          <img
                            src={signatureImage}
                            alt="Signature"
                            className="members-id-signature-img"
                          />
                        </button>
                      )}
                      <div className="members-id-signature-line" />
                      <span className="members-id-signature-label">Authorized Signature</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* BACK */}
              <div className="digital-id-card__face digital-id-card__face--back">
                <div className="digital-id-card__header digital-id-card__header--back" style={{ background: branding.colors.headerBackGradient }}>
                  <div className="digital-id-card__seal">
                    <img src={branding.logo} alt={branding.shortName} />
                  </div>
                  <div>
                    <p>{branding.shortName} DIGITAL ID</p>
                    <span>Official Verification Code</span>
                  </div>
                </div>
                <div className="digital-id-card__qr">
                  <div className="digital-id-card__qr-box">
                    {generatedQr ? (
                      <img src={generatedQr} alt="Membership QR" />
                    ) : (
                      <QrCode size={168} />
                    )}
                  </div>
                  <div className="digital-id-card__qr-info">
                    <span>{cardMemberId}</span>
                    <span className="members-id-qr-ref">QR Ref: {qrReference}</span>
                    <small>Scan to verify membership status</small>
                  </div>
                  <div className="digital-id-card__emergency-contact">
                    <strong>Emergency Contact</strong>
                    <span>Name: {emergencyContactName}</span>
                    <span>Mobile No: {emergencyContactMobile}</span>
                  </div>
                </div>
                <div className="digital-id-card__footer" style={{ background: branding.colors.footerGradient }}>
                  <ShieldCheck size={16} />
                  <span>LaborConnect © 2026 • Official Digital ID</span>
                </div>
              </div>
            </div>
          </div>
          <div className="admin-id-panel">
            <div className={`admin-id-status ${idIssued ? "is-active" : "is-pending"}`}>
              <ShieldCheck size={16} />
              <span>{resolvedIdStatus}</span>
            </div>
            <p className="admin-muted">
              {idIssued
                ? "Digital and physical credentials are active for this member."
                : "No finalized ID detected. Issue a card to sync this preview."}
            </p>
            <ul className="admin-id-info">
              <li>
                <span>Digital ID</span>
                <strong>{member.digitalID ?? "Pending assignment"}</strong>
              </li>
              <li>
                <span>Physical card</span>
                <strong>{physicalCardStatus}</strong>
              </li>
              <li>
                <span>Last update</span>
                <strong>{formatDate(idLastUpdated)}</strong>
              </li>
              <li>
                <span>Request ref.</span>
                <strong>{idRequestReference}</strong>
              </li>
            </ul>
            <div className="admin-id-panel__actions">
              <button
                type="button"
                className={`admin-button ${idIssued ? "" : "is-primary"}`.trim()}
                onClick={() => navigate(`/admin/id-card-management?member=${encodeURIComponent(member.memberID ?? "")}`)}
              >
                {idIssued ? "Update card" : "Issue ID"}
              </button>
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={() => navigate("/admin/id-card-management")}
              >
                View ID queue
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="admin-surface admin-stack-md member-profile-section member-profile-section--details">
        <h2>Personal & employment</h2>
        <div className="admin-detail-grid admin-detail-grid--spacious">
          <article className="admin-card admin-card--panel">
            <div className="admin-card__label">
              <User size={16} /> Personal information
            </div>
            <div className="admin-stat">
              <span>Date of birth</span>
              <span>{formatDate(member.dateOfBirth, { month: "long", day: "numeric", year: "numeric" })}</span>
            </div>
            <div className="admin-stat">
              <span>Place of birth</span>
              <span>{member.placeOfBirth ?? "—"}</span>
            </div>
            <div className="admin-stat">
              <span>Address</span>
              <span>{member.address ?? "—"}</span>
            </div>
            <div className="admin-stat">
              <span>Years employed</span>
              <span>{formatYearsEmployed(member.yearsEmployed)}</span>
            </div>
            <div className="admin-stat">
              <span>Educational attainment</span>
              <span>{member.education ?? "—"}</span>
            </div>
            <div className="admin-stat">
              <span>Religion</span>
              <span>{member.religion ?? "—"}</span>
            </div>
            <div className="admin-stat">
              <span>No. of children</span>
              <span>{member.numberOfChildren ?? "—"}</span>
            </div>
          </article>

          <article className="admin-card admin-card--panel">
            <div className="admin-card__label">
              <Building2 size={16} /> Employment details
            </div>
            <div className="admin-stat">
              <span>Company</span>
              <span>{member.company ?? "—"}</span>
            </div>
            <div className="admin-stat">
              <span>Union name</span>
              <span>{member.unionAffiliation ?? "—"}</span>
            </div>
            <div className="admin-stat">
              <span>Position</span>
              <span>{member.position ?? "—"}</span>
            </div>
          </article>

          <article className="admin-card admin-card--panel">
            <div className="admin-card__label">
              <Users size={16} /> Emergency contact
            </div>
            <div className="admin-stat">
              <span>Contact</span>
              <span>{emergencyContact?.fullName ?? member.emergencyContactSummary ?? "Not provided"}</span>
            </div>
            <div className="admin-stat">
              <span>Relationship</span>
              <span>{emergencyContact?.relationship ?? "—"}</span>
            </div>
            <div className="admin-stat">
              <span>Contact number</span>
              <span>{emergencyContact?.phone ?? "—"}</span>
            </div>
            <div className="admin-stat">
              <span>Address</span>
              <span>{emergencyContact?.address ?? "—"}</span>
            </div>
          </article>

          <article className="admin-card admin-card--panel">
            <div className="admin-card__label">
              <Calendar size={16} /> Membership timeline
            </div>
            <div className="admin-stat">
              <span>Registered</span>
              <span>{formatDate(member.registeredDate ?? member.joinDate)}</span>
            </div>
            <div className="admin-stat">
              <span>Approved</span>
              <span>{formatDate(member.approvedAt) ?? "—"}</span>
            </div>
            <div className="admin-stat">
              <span>Status</span>
              <span>{member.status}</span>
            </div>
          </article>
        </div>
      </section>

      <section className="admin-surface admin-stack-md member-profile-section member-profile-section--documents">
        <header className="admin-row">
          <div className="admin-section-heading">
            <span className="admin-heading-icon admin-heading-icon--dues">
              <FileText size={16} />
            </span>
            <div>
              <h2>Documents</h2>
              <p className="admin-muted">Key files uploaded for this member.</p>
            </div>
          </div>
        </header>
        <div className="admin-detail-grid">
          {documents.map((doc) => {
            const docUrl = resolveDocumentUrl(doc.filePath);
            const canPreviewAsImage = isImageDocument(doc, docUrl);
            const visibleStatus = getVisibleDocumentStatus(doc.status);
            return (
              <article key={doc.name} className="admin-card">
                <div className="admin-card__label">{doc.type}</div>
                <div className="admin-card__value members-doc-value">{doc.name}</div>
                <div className="admin-card__meta">
                  Uploaded {formatDate(doc.uploadedAt)}
                  {visibleStatus ? ` • ${visibleStatus}` : ""}
                </div>
                <div className="admin-pill-group">
                  <button
                    type="button"
                    className="admin-button admin-button--ghost"
                    onClick={() => {
                      if (!docUrl) return;
                      if (canPreviewAsImage) {
                        openMediaModal(docUrl, doc.name || doc.type || "Document Preview", {
                          filePath: doc.filePath ?? "",
                          uploadedAt: doc.uploadedAt ?? "",
                          source: "Member documents",
                        });
                        return;
                      }
                      window.open(docUrl, "_blank", "noopener,noreferrer");
                    }}
                    disabled={!docUrl}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button--ghost"
                    onClick={() => {
                      if (docUrl) window.open(docUrl, "_blank", "noopener,noreferrer");
                    }}
                    disabled={!docUrl}
                  >
                    Download
                  </button>
                </div>
                {!fileBaseUrl && doc.filePath ? (
                  <p className="admin-muted members-file-hint">
                    File path: {doc.filePath}
                  </p>
                ) : null}
              </article>
            );
          })}
          {documents.length === 0 ? (
            <div className="admin-empty-state">No documents uploaded.</div>
          ) : null}
        </div>
      </section>

      <section className="admin-surface admin-stack-md member-profile-section member-profile-section--activity">
        <header className="admin-row">
          <div className="admin-section-heading">
            <span className="admin-heading-icon admin-heading-icon--dues">
              <Clock size={16} />
            </span>
            <div>
              <h2>Recent activity</h2>
              <p className="admin-muted">System activity associated with this member.</p>
            </div>
          </div>
        </header>
        <div className="admin-list members-activity-list">
          {activitiesLoading ? (
            <div className="admin-empty-state">Loading recent activity...</div>
          ) : recentActivities.length ? (
            recentActivities.map((activity, index) => {
              if (activity.type === "ticket") {
                const ticket = activity.ticket;
                const ticketId = ticket.ticketNo ?? ticket.ticketId ?? `#${ticket.id ?? "—"}`;
                return (
                  <div key={`ticket-${activity.id ?? index}`} className="admin-card members-activity-card members-activity-card--ticket">
                    <div className="admin-row members-activity-row members-activity-row--header">
                      <span className="admin-pill is-blue">[TICKET]</span>
                      <strong className="members-activity-id">{ticketId}</strong>
                    </div>
                    <div className="members-activity-details">
                      <p className="members-activity-line"><span>Category</span><strong>{toTitleCaseWords(ticket.category ?? ticket.type)}</strong></p>
                      <p className="members-activity-line"><span>Priority</span><strong>{toTitleCaseWords(ticket.priority)}</strong></p>
                      <p className="members-activity-line"><span>Department</span><strong>{toTitleCaseWords(ticket.department ?? ticket.departmentName)}</strong></p>
                      <p className="members-activity-line"><span>Status</span><strong>{toTitleCaseWords(ticket.status)}</strong></p>
                      <p className="members-activity-line">
                        <span>Date Created</span>
                        <strong>{formatDateTimeValue(ticket.createdAt ?? ticket.created_at, { month: "short", day: "numeric", year: "numeric" })}</strong>
                      </p>
                    </div>
                  </div>
                );
              }

              const booking = activity.booking;
              const bookingStatus = (() => {
                const normalized = normalizeStatusKey(booking.status);
                if (normalized === "pending") return "Pending Approval";
                return toTitleCaseWords(booking.status);
              })();

              return (
                <div key={`booking-${activity.id ?? index}`} className="admin-card members-activity-card members-activity-card--booking">
                  <div className="admin-row members-activity-row members-activity-row--header">
                    <span className="admin-pill is-green">[BOOKING]</span>
                    <strong className="members-activity-id">#{booking.id ?? booking.bookingId ?? "—"}</strong>
                  </div>
                  <div className="members-activity-details">
                    <p className="members-activity-line"><span>Venue Name</span><strong>{booking.venueName ?? "—"}</strong></p>
                    <p className="members-activity-line">
                      <span>Booking Date</span>
                      <strong>{formatDateTimeValue(booking.bookingDate, { month: "short", day: "numeric", year: "numeric" })}</strong>
                    </p>
                    <p className="members-activity-line"><span>Time</span><strong>{formatTimeRange(booking.startTime, booking.endTime)}</strong></p>
                    <p className="members-activity-line"><span>Booking Status</span><strong>{bookingStatus}</strong></p>
                    <p className="members-activity-line"><span>Booking ID</span><strong>#{booking.id ?? booking.bookingId ?? "—"}</strong></p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="admin-empty-state">No recent activity found for this member.</div>
          )}
        </div>
      </section>

      {mediaModal.open ? (
        <div
          className="admin-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={mediaModal.label || "Image preview"}
        >
          <div className="admin-dialog__backdrop" onClick={closeMediaModal} />
          <div className="admin-dialog__panel" role="document">
            <header className="admin-dialog__header">
              <h3>{mediaModal.label || "Document preview"}</h3>
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={closeMediaModal}
                aria-label="Close image preview"
              >
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body">
              <div className="admin-document-preview members-media-preview-frame">
                <img src={mediaModal.src} alt={mediaModal.label || "Preview"} />
              </div>
              <div className="admin-stack-sm">
                <div>
                  <strong>Stored path</strong>
                  <p className="admin-muted">{mediaModal.filePath || "Not available"}</p>
                </div>
                <div>
                  <strong>Date uploaded</strong>
                  <p className="admin-muted">{mediaModal.uploadedAt ? formatDateTimeValue(mediaModal.uploadedAt, { month: "short", day: "numeric", year: "numeric" }) : "Not available"}</p>
                </div>
                <div>
                  <strong>Source</strong>
                  <p className="admin-muted">{mediaModal.source || "Unknown source"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {formPreview.open ? (
        <div
          className="admin-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Member form preview"
        >
          <div className="admin-dialog__backdrop" onClick={closeFormPreview} />
          <div className="admin-dialog__panel" role="document">
            <header className="admin-dialog__header">
              <h3>Member Form Preview</h3>
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={closeFormPreview}
                aria-label="Close form preview"
              >
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body">
              {formPreview.error ? (
                <div className="admin-alert is-error">{formPreview.error}</div>
              ) : null}
              {!formPreview.error && !formPreview.url ? (
                <div className="admin-empty-state">Generating form preview...</div>
              ) : null}
              {formPreview.url ? (
                <iframe
                  src={formPreview.url}
                  title="Member form preview"
                  style={{ width: "100%", minHeight: "72vh", border: "1px solid var(--admin-card-border)", borderRadius: "12px" }}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
